/**
 * Filtro y resumen del bloque de asignaciones: funciones PURAS, sin React.
 *
 * Viven aparte del componente por lo mismo que `grafoLayout.ts`: se pueden
 * afirmar sin montar nada (React Flow no pinta en jsdom), y la regla eslint
 * `react-refresh/only-export-components` no deja exportarlas desde un fichero
 * de componente. Las usan LAS DOS VISTAS —grafo y tabla— para que el filtro
 * signifique lo mismo en las dos: dos implementaciones darian dos respuestas a
 * "¿que casa?" y la vista accesible seria la que se queda desactualizada.
 */
import type { Asignacion } from "@/lib/api/modelosApi";

/**
 * Normaliza un texto para comparar: sin mayusculas y sin acentos.
 *
 * Sin quitar acentos, buscar "operacion" no encontraria "operación" y al reves,
 * y aqui conviven etiquetas en dos idiomas y `target_id` tecnicos en ingles.
 * `NFD` + borrado de diacriticos es lo que hace que las dos formas casen.
 */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * ¿Casa este target con el texto del filtro?
 *
 * Se mira el `label` (lo que se ve) Y el `target_id` (lo que se configura y lo
 * que el backend entiende). Solo por el label, buscar
 * `especificaciones/redactor` —que es lo que alguien copia de un error del
 * backend— no encontraria nada, porque el label de un subagente viene SIN el
 * prefijo del padre.
 */
export function casa(a: Asignacion, consulta: string): boolean {
  const q = normalizar(consulta);
  return normalizar(a.label).includes(q) || normalizar(a.target_id).includes(q);
}

/** Papel de un target tras aplicar el filtro. */
export type Coincidencia = "casa" | "contexto";

export interface Filtrado {
  /** Los targets que se siguen pintando, en el ORDEN de entrada. */
  visibles: Asignacion[];
  /**
   * Para cada `target_id` visible, si casa de verdad o solo esta de contexto.
   *
   * Es lo que permite atenuar al padre que no casa en vez de esconderlo, sin
   * que la vista tenga que volver a decidir quien casaba.
   */
  papel: Map<string, Coincidencia>;
  /** Cuantos casan de verdad. Es el numero que se le enseña al usuario. */
  numCasan: number;
}

/**
 * Aplica el filtro conservando la estructura.
 *
 * **El caso que decide el diseño: un subagente que casa cuyo padre no casa.**
 * Esconder al padre dejaria al hijo huerfano —sin arista en el grafo, sin fila
 * encima en la tabla— y perderia justo el dato que esta pantalla existe para
 * enseñar: de quien hereda el modelo. Peor aun, el hijo se pintaria en la zona
 * de huerfanos del layout, insinuando una anomalia que no existe. Asi que el
 * padre **se conserva, atenuado y marcado como contexto**: se ve de donde
 * cuelga el resultado, y a la vez se ve que el que buscabas es el hijo.
 *
 * Al reves no hay regla ninguna, y no hace falta: el `target_id` de un
 * subagente ES `<padre>/<hijo>`, asi que buscar el nombre del agente ya casa
 * con sus hijos por id, sin codigo que lo fuerce. Y es lo que se quiere —
 * buscar `especificaciones` devuelve su bloque entero, con las aristas—, pero
 * conviene saber que sale de la FORMA del id y no de una decision: un dia en
 * que el `target_id` deje de llevar el prefijo, esto cambia solo.
 *
 * Una consulta en blanco devuelve todo, con todos marcados `casa`: sin filtro
 * no hay nada atenuado.
 */
export function filtrarAsignaciones(asignaciones: Asignacion[], consulta: string): Filtrado {
  const q = consulta.trim();
  if (!q) {
    return {
      visibles: asignaciones,
      papel: new Map(asignaciones.map((a) => [a.target_id, "casa" as const])),
      numCasan: asignaciones.length,
    };
  }

  const papel = new Map<string, Coincidencia>();
  for (const a of asignaciones) {
    if (casa(a, q)) papel.set(a.target_id, "casa");
  }
  const numCasan = papel.size;

  // Los padres de los subagentes que casan entran como contexto, si no casaban
  // ya por su cuenta (un `set` aqui degradaria a "contexto" a uno que casa).
  for (const a of asignaciones) {
    if (a.target_type !== "subagent" || !a.parent) continue;
    if (papel.get(a.target_id) !== "casa") continue;
    if (!papel.has(a.parent)) papel.set(a.parent, "contexto");
  }

  return {
    visibles: asignaciones.filter((a) => papel.has(a.target_id)),
    papel,
    numCasan,
  };
}

export interface ResumenAsignaciones {
  /** Targets con fila propia en `llm_assignments`. "Lo que he tocado yo". */
  asignados: number;
  /** Targets que corren con el default del codigo. */
  porDefecto: number;
  /** Subagentes que siguen el modelo de su agente. */
  heredados: number;
  total: number;
}

/**
 * Cuenta los targets por origen.
 *
 * Los tres origenes se cuentan por separado y no se colapsan "heredado" dentro
 * de "por defecto": un subagente heredado corre con un modelo que alguien
 * ELIGIO (el de su padre), y meterlo en el saco del default diria que nadie ha
 * tocado nada donde si se ha tocado. Es justo la pregunta que el resumen
 * responde.
 *
 * Cuenta sobre la lista que recibe: se le pasa la COMPLETA, no la filtrada. Un
 * resumen que cambiara con el filtro dejaria de responder "¿que he tocado en
 * este panel?" para responder "¿que he tocado de lo que estoy mirando?", que no
 * es lo que se pregunta al mirar la cabecera.
 */
export function resumirAsignaciones(asignaciones: Asignacion[]): ResumenAsignaciones {
  let asignados = 0;
  let heredados = 0;
  for (const a of asignaciones) {
    // Se lee el `origen` CRUDO y no `origenConocido`: un origen que este front
    // no conozca no debe contarse como asignado ni como heredado, y el `else`
    // lo manda a `porDefecto`, que es el trato conservador de `origenConocido`.
    if (a.origen === "asignado") asignados++;
    else if (a.origen === "heredado") heredados++;
  }
  return {
    asignados,
    heredados,
    porDefecto: asignaciones.length - asignados - heredados,
    total: asignaciones.length,
  };
}

/**
 * Subagentes que un cambio en `agente` ARRASTRARIA: los que no tienen
 * asignacion propia.
 *
 * Es la consecuencia no evidente de la precedencia elegida (gana lo mas
 * especifico): asignar un modelo al padre mueve a los heredados y deja quieto
 * al que tiene el suyo. Sin decirlo, un admin que cambia `especificaciones`
 * cree que toca un target y toca cuatro.
 *
 * Se mira que NO sea `asignado` en vez de que SEA `heredado`: un subagente en
 * `default` (su padre tampoco tiene asignacion) tambien pasara a seguir al
 * padre en cuanto el padre la tenga. Contar solo los `heredado` diria "afecta a
 * 0" en el caso mas comun de todos — la primera vez que se asigna algo.
 */
export function subagentesArrastrados(
  asignaciones: Asignacion[],
  agenteId: string,
): Asignacion[] {
  return asignaciones.filter((a) => arrastra(a, agenteId));
}

/**
 * Cuantos arrastraria cada agente, en una pasada.
 *
 * La usan LAS DOS vistas para pintar el aviso; comparte el predicado `arrastra`
 * con `subagentesArrastrados` (que es lo que `asignar` usa para saber a quien
 * resaltar) a proposito: con dos criterios, el panel prometeria un numero y
 * resaltaria otro conjunto, y nadie lo notaria hasta que no cuadraran.
 *
 * Se cuenta sobre la lista que recibe, que es la VISIBLE: si el filtro esconde
 * a un hijo, contarlo prometeria un efecto sobre algo que no esta en pantalla.
 */
export function contarArrastres(asignaciones: Asignacion[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const a of asignaciones) {
    if (!a.parent || !arrastra(a, a.parent)) continue;
    m.set(a.parent, (m.get(a.parent) ?? 0) + 1);
  }
  return m;
}

/** ¿Arrastraria a `a` un cambio de modelo en el agente `agenteId`? */
function arrastra(a: Asignacion, agenteId: string): boolean {
  return a.target_type === "subagent" && a.parent === agenteId && a.origen !== "asignado";
}
